// Orders Page - Order history for engineers
import { useState, useEffect, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Package, Truck, ClipboardList, Search, Filter, X, AlertCircle } from 'lucide-react'
import clsx from 'clsx'
import { EngineerLayout } from '../../components/layout/EngineerLayout'
import { Button, Pagination } from '../../components/radix'
import { useOrders } from '../../auth/hooks/useOrders'
import { useWeeklySpend } from '../../auth/hooks/useWeeklySpend'
import { mapOrderForEngineer } from '../../services/order-mappers'
import { ORDER_STATUS_OPTIONS, HIDDEN_STATUS_VALUES, getStatusLabel, getStatusFilterMatches } from '../../constants/orderStatus'
import { formatDate, formatDateTimeSmart } from '../../utils/datetime'
import { formatMoney } from '../../utils/currency'
import type { EngineerOrder } from '../../types'

interface EngineerSession {
  postcode: string
  latitude: number
  longitude: number
}

// Statuses where the order never went ahead, so the APR-011 change note is moot.
const HIDE_ITEM_AVAILABILITY_STATUSES: EngineerOrder['status'][] = [
  'fulfillment_declined',
  'supplier_cancelled',
]

// Format date with time — UK timezone, with relative "Today/Yesterday" labels
const formatDateTime = formatDateTimeSmart

export function OrdersPage() {
  const navigate = useNavigate()
  const { tenantSlug } = useParams()
  const [session, setSession] = useState<EngineerSession | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedSupplier, setSelectedSupplier] = useState<string>('all')
  const [selectedDateRange, setSelectedDateRange] = useState<string>('all')
  const [customDateFrom, setCustomDateFrom] = useState<string>('')
  const [customDateTo, setCustomDateTo] = useState<string>('')
  const [selectedStatus, setSelectedStatus] = useState<string>('all')
  const [selectedFulfillment, setSelectedFulfillment] = useState<string>('all')
  const [currentPage, setCurrentPage] = useState(1)
  const PAGE_SIZE = 20

  const { data: weeklySpend } = useWeeklySpend()
  const weeklySpent = Number(weeklySpend?.spent ?? 0)
  const weeklyCap = Number(weeklySpend?.weekly_cap ?? 0)
  const weeklyRingPct = weeklyCap > 0 ? Math.round(Math.min(100, (weeklySpent / weeklyCap) * 100)) : 0

  // Fetch all orders at once so client-side filters work across the full history
  const { data, isLoading, error } = useOrders(undefined, 1, true, 500)
  const allOrders: EngineerOrder[] = useMemo(
    () => (data?.results || []).map(mapOrderForEngineer),
    [data]
  )

  // Get unique suppliers for filter
  const suppliers = useMemo(() => {
    const uniqueSuppliers = Array.from(new Set(allOrders.map(o => o.supplierName)))
    return uniqueSuppliers.sort()
  }, [allOrders])

  // Filter orders (full filtered list for counts and pagination)
  const orders = useMemo(() => {
    let filtered = [...allOrders]

    // Search by order number or supplier
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim()
      filtered = filtered.filter(o =>
        o.orderNumber.toLowerCase().includes(query) ||
        o.supplierName.toLowerCase().includes(query) ||
        o.branchName.toLowerCase().includes(query)
      )
    }

    // Filter by supplier
    if (selectedSupplier !== 'all') {
      filtered = filtered.filter(o => o.supplierName === selectedSupplier)
    }

    // Filter by date range
    if (selectedDateRange === 'custom') {
      if (customDateFrom) {
        filtered = filtered.filter(o => new Date(o.placedAt) >= new Date(customDateFrom))
      }
      if (customDateTo) {
        const toDate = new Date(customDateTo)
        toDate.setHours(23, 59, 59, 999)
        filtered = filtered.filter(o => new Date(o.placedAt) <= toDate)
      }
    } else if (selectedDateRange !== 'all') {
      const daysAgo = parseInt(selectedDateRange)
      const cutoffDate = new Date()
      cutoffDate.setDate(cutoffDate.getDate() - daysAgo)
      filtered = filtered.filter(o => new Date(o.placedAt) >= cutoffDate)
    }

    // Filter by status. Merged labels (e.g. "Sent to supplier" covers both
    // started_processing and awaiting_supplier) surface all their backend
    // values together via getStatusFilterMatches — the same expansion the
    // backend applies for the admin queue.
    if (selectedStatus !== 'all') {
      const matches = getStatusFilterMatches(selectedStatus)
      filtered = filtered.filter((o) => matches.includes(o.status))
    }

    // Filter by fulfillment type
    if (selectedFulfillment !== 'all') {
      filtered = filtered.filter(o => o.fulfillmentType === selectedFulfillment)
    }

    return filtered
  }, [allOrders, searchQuery, selectedSupplier, selectedDateRange, customDateFrom, customDateTo, selectedStatus, selectedFulfillment])

  // Client-side pagination over the filtered list
  const totalCount = orders.length
  const totalPages = Math.ceil(totalCount / PAGE_SIZE)
  const hasNextPage = currentPage < totalPages
  const hasPrevPage = currentPage > 1
  const paginatedOrders = useMemo(
    () => orders.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [orders, currentPage]
  )

  // Load session on mount (optional — orders don't require postcode)
  useEffect(() => {
    const savedSession = sessionStorage.getItem('engineerSession')
    if (savedSession) {
      setSession(JSON.parse(savedSession))
    }
  }, [])

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [searchQuery, selectedSupplier, selectedDateRange, customDateFrom, customDateTo, selectedStatus, selectedFulfillment])

  // Check if any filters are active
  const hasActiveFilters = selectedSupplier !== 'all' || selectedDateRange !== 'all' || selectedStatus !== 'all' || selectedFulfillment !== 'all' || searchQuery.trim() !== '' || !!customDateFrom || !!customDateTo

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery('')
    setSelectedSupplier('all')
    setSelectedDateRange('all')
    setCustomDateFrom('')
    setCustomDateTo('')
    setSelectedStatus('all')
    setSelectedFulfillment('all')
  }

  // Loading state
  if (isLoading) {
    return (
      <EngineerLayout hideHeader>
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary" />
        </div>
      </EngineerLayout>
    )
  }

  // Error state
  if (error) {
    return (
      <EngineerLayout hideHeader>
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700 text-sm">{error.message || 'Failed to load orders'}</p>
        </div>
      </EngineerLayout>
    )
  }

  // Status configuration
  const getStatusConfig = (status: EngineerOrder['status']) => {
    const label = getStatusLabel(status)
    switch (status) {
      // Needs Attention (Amber)
      case 'awaiting_approval':
        return { label, color: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' }

      // Submitting (Sky) — retry loop before the supplier acknowledges.
      case 'started_processing':
        return { label, color: 'bg-sky-100 text-sky-700', dot: 'bg-sky-500' }

      // Approved / Sent to supplier (Blue) — tenant approved, or supplier
      // has the order and we're awaiting their ack.
      case 'approved':
      case 'awaiting_supplier':
        return { label, color: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' }

      // Processing (Indigo)
      case 'processing':
        return { label, color: 'bg-indigo-100 text-indigo-700', dot: 'bg-indigo-500' }

      // In Transit (Purple)
      case 'out_for_delivery':
        return { label, color: 'bg-purple-100 text-purple-700', dot: 'bg-purple-500' }

      // Success (Green)
      case 'ready_to_collect':
      case 'completed':
        return { label, color: 'bg-green-100 text-green-700', dot: 'bg-green-500' }

      // Failed/Declined (Red)
      case 'submission_failed':
      case 'internal_decline':
      case 'fulfillment_declined':
        return { label, color: 'bg-red-100 text-red-700', dot: 'bg-red-500' }

      // Legacy: collapsed into fulfillment_declined for new orders.
      // Existing rows share the "Declined by supplier" label (resolved via
      // getStatusLabel) so users see one term regardless of order age.
      case 'supplier_cancelled':
        return { label, color: 'bg-red-100 text-red-700', dot: 'bg-red-500' }

      // Withdrawn by the engineer (muted gray)
      case 'withdrawn':
        return { label, color: 'bg-gray-100 text-gray-500', dot: 'bg-gray-400' }

      // Cancelled (Gray)
      case 'cancelled':
        return { label, color: 'bg-gray-100 text-gray-700', dot: 'bg-gray-500' }

      default:
        return { label, color: 'bg-gray-100 text-gray-700', dot: 'bg-gray-500' }
    }
  }

  return (
    <EngineerLayout hideHeader>
      {/* Header — themed via `.bg-nav` / `.text-nav`. Shared EngineerHeader
       * suppressed on Orders: this page shows title + weekly gauge instead
       * of the postcode pill (matches the mockup for top-level Orders). */}
      <div className="bg-nav text-nav border-b border-nav-divider sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <h1 className="text-nav font-bold text-lg">Orders</h1>
          {/* Weekly spend ring (APR-012 AC1). Hidden when no cap (AC3). */}
          {weeklySpend?.has_cap && (
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="text-right">
                <span className="text-[11px] text-nav-inactive block leading-none">Weekly</span>
                {/* Rounded to whole pounds to match the mockup and the shared
                 * WeeklySpendGauge — `formatMoney` was showing 2dp whenever
                 * the WTD spend had pence, drifting from the Home tab. */}
                <span className="text-xs tabular-nums font-bold text-nav">
                  £{Math.round(weeklySpent)}
                  <span className="text-nav-inactive font-normal"> / £{Math.round(weeklyCap)}</span>
                </span>
              </div>
              <div className="w-12 h-12 relative">
                <svg className="w-12 h-12 -rotate-90" viewBox="0 0 36 36">
                  <circle
                    cx="18"
                    cy="18"
                    r="15"
                    fill="none"
                    stroke="currentColor"
                    strokeOpacity={0.2}
                    strokeWidth="3"
                  />
                  <circle
                    cx="18"
                    cy="18"
                    r="15"
                    fill="none"
                    stroke="#fbbf24"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeDasharray={`${weeklyRingPct * 0.94} ${100 - weeklyRingPct * 0.94}`}
                  />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-nav">
                  {weeklyRingPct}%
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Search Bar — sticks below the page header (~52px tall). */}
      <div className="bg-white border-b border-gray-200 sticky top-[52px] z-10">
        <div className="max-w-3xl mx-auto px-4 py-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by order ID or supplier..."
              className="w-full h-11 pl-10 pr-10 text-base text-gray-900 bg-gray-50 border border-gray-200 rounded-xl placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filters — sticks below page header (~52px) + search bar (~64px). */}
      <div className="bg-gray-50 border-b border-gray-200 sticky top-[116px] z-10">
        <div className="max-w-3xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <span className="text-gray-600 text-xs font-medium uppercase tracking-wide">Filters</span>
            </div>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="text-brand-primary text-xs font-medium hover:underline"
              >
                Clear all
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {/* Status Filter */}
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="h-10 px-3 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary appearance-none bg-no-repeat bg-right pr-8"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                backgroundPosition: 'right 0.5rem center',
                backgroundSize: '1.25em 1.25em',
              }}
            >
              <option value="all">Order status</option>
              {ORDER_STATUS_OPTIONS.filter((option) => !HIDDEN_STATUS_VALUES.has(option.value)).map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>

            {/* Supplier Filter */}
            <select
              value={selectedSupplier}
              onChange={(e) => setSelectedSupplier(e.target.value)}
              className="h-10 px-3 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary appearance-none bg-no-repeat bg-right pr-8"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                backgroundPosition: 'right 0.5rem center',
                backgroundSize: '1.25em 1.25em',
              }}
            >
              <option value="all">Suppliers</option>
              {suppliers.map((supplier) => (
                <option key={supplier} value={supplier}>
                  {supplier}
                </option>
              ))}
            </select>

            {/* Fulfillment Filter */}
            <select
              value={selectedFulfillment}
              onChange={(e) => setSelectedFulfillment(e.target.value)}
              className="h-10 px-3 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary appearance-none bg-no-repeat bg-right pr-8"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                backgroundPosition: 'right 0.5rem center',
                backgroundSize: '1.25em 1.25em',
              }}
            >
              <option value="all">Delivery or collection</option>
              <option value="delivery">Delivery</option>
              <option value="pickup">Collection</option>
            </select>

            {/* Date Range Filter */}
            <select
              value={selectedDateRange}
              onChange={(e) => setSelectedDateRange(e.target.value)}
              className={clsx(
                'h-10 px-3 text-sm text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary appearance-none bg-no-repeat bg-right pr-8',
                selectedDateRange === 'custom' ? 'bg-gray-100' : 'bg-white'
              )}
              style={{
                backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                backgroundPosition: 'right 0.5rem center',
                backgroundSize: '1.25em 1.25em',
              }}
            >
              <option value="all">Order date</option>
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="custom">Custom range…</option>
            </select>
          </div>

          {/* Custom date range inputs — shown only when "Custom range…" is selected */}
          {selectedDateRange === 'custom' && (
            <div className="mt-2 bg-gray-100 rounded-lg px-3 py-2">
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={customDateFrom}
                  onChange={(e) => setCustomDateFrom(e.target.value)}
                  className="flex-1 h-10 px-3 text-sm text-gray-900 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                />
                <span className="text-gray-400 text-sm">to</span>
                <input
                  type="date"
                  value={customDateTo}
                  min={customDateFrom || undefined}
                  onChange={(e) => setCustomDateTo(e.target.value)}
                  className="flex-1 h-10 px-3 text-sm text-gray-900 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <main className="max-w-3xl mx-auto px-4 py-6 pb-24">
        {/* Results Count */}
        {allOrders.length > 0 && (
          <div className="mb-4">
            <p className="text-gray-500 text-sm">
              <span className="font-medium text-gray-600">{orders.length}</span> order{orders.length !== 1 ? 's' : ''}
            </p>
          </div>
        )}

        {orders.length === 0 ? (
          // Empty State
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mb-6">
              <ClipboardList className="w-10 h-10 text-gray-300" />
            </div>
            <h2 className="text-gray-900 text-xl font-bold mb-3 text-center">
              {allOrders.length === 0 ? "You haven't placed any orders yet" : 'No orders found'}
            </h2>
            <p className="text-gray-500 text-base text-center mb-6 max-w-sm">
              {allOrders.length === 0
                ? 'Search for products to get started.'
                : 'Try adjusting your filters to see more orders.'}
            </p>
            {allOrders.length === 0 ? (
              <Button onClick={() => navigate(`/${tenantSlug}/engineer/search`)} size="lg">
                <Search className="w-5 h-5 mr-2" />
                Search products
              </Button>
            ) : (
              <Button onClick={clearFilters} variant="outline" size="lg">
                Clear filters
              </Button>
            )}
          </div>
        ) : (
          // Orders List
          <div className="space-y-4">
            {paginatedOrders.map((order) => {
              const statusConfig = getStatusConfig(order.status)

              return (
                <button
                  key={order.id}
                  onClick={() => navigate(`/${tenantSlug}/engineer/order/${order.id}`)}
                  className="w-full text-left bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden hover:border-gray-300 hover:shadow-md transition-all"
                >
                  <div className="p-4 sm:p-5">
                    {/* Header Row */}
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-gray-900 font-bold text-lg mb-1">
                          Order# {order.orderNumber}
                        </h3>
                        <p className="text-gray-400 text-xs">
                          {formatDateTime(order.placedAt)}
                        </p>
                      </div>
                      <span
                        className={clsx(
                          'inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full',
                          statusConfig.color
                        )}
                      >
                        <span className={clsx('w-1.5 h-1.5 rounded-full', statusConfig.dot)} />
                        {statusConfig.label}
                      </span>
                    </div>

                    {/* Supplier Info */}
                    <p className="text-gray-600 text-sm mb-3">
                      {order.supplierName}{order.supplierIsActive === false && <span className="text-red-500 text-xs ml-1">(Inactive)</span>} - {order.branchName}
                    </p>

                    {/* Fulfillment & Summary Row */}
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                      <div className="flex items-center gap-3">
                        {order.fulfillmentType === 'pickup' ? (
                          <div className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-700 px-2.5 py-1 rounded-lg text-xs font-medium">
                            <Package className="w-3.5 h-3.5" />
                            Collection
                          </div>
                        ) : (
                          <div className="inline-flex items-center gap-1.5 bg-green-50 text-green-700 px-2.5 py-1 rounded-lg text-xs font-medium">
                            <Truck className="w-3.5 h-3.5" />
                            Delivery
                          </div>
                        )}
                        <span className="text-gray-500 text-sm">
                          <span className="font-medium text-gray-600">{order.itemCount}</span> item{order.itemCount !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <span className="text-gray-900 font-bold text-lg">
                        £{order.total.toFixed(2)}
                      </span>
                    </div>

                    {/* Expected fulfilment date — small inline metadata, "-"
                        when supplier didn't share a date so the row stays
                        consistent across orders. */}
                    <div className="text-gray-500 text-xs mt-2">
                      Expected {order.fulfillmentType === 'pickup' ? 'collection' : 'delivery'}:{' '}
                      <span className="font-medium text-gray-700">
                        {order.expectedFulfillmentDate ? formatDate(order.expectedFulfillmentDate) : '-'}
                      </span>
                    </div>

                    {/* Collection guidance (DGM-003) — backend decides visibility. */}
                    {order.collectionGuidance && (
                      <p className="text-blue-600 text-xs mt-1 leading-snug">
                        {order.collectionGuidance.text}
                      </p>
                    )}
                    {/* Delivery guidance (DGM-004) — backend decides visibility. */}
                    {order.deliveryGuidance && (
                      <p className="text-blue-600 text-xs mt-1 leading-snug">
                        {order.deliveryGuidance.text}
                      </p>
                    )}

                    {/* Status note (mock parity): amber "sent for approval" while
                        awaiting. Decline reason lives on the detail page only. */}
                    {order.status === 'awaiting_approval' && (
                      <p className="text-amber-700 text-xs mt-2 font-medium">
                        {order.assigned_approver
                          ? `Sent to ${order.assigned_approver} for approval`
                          : 'Sent for approval'}
                      </p>
                    )}

                    {/* APR-011 AC4: flag when approval changed item availability
                        so the engineer knows to open the order for detail. Hidden
                        on orders that never went ahead (supplier declined/
                        cancelled) — the changes are moot there. */}
                    {!HIDE_ITEM_AVAILABILITY_STATUSES.includes(order.status) &&
                      (order.outOfStockCount > 0 || order.dateChangedCount > 0) && (
                      <p className="inline-flex items-center gap-1.5 text-amber-700 text-xs mt-2 font-medium">
                        <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                        {[
                          order.outOfStockCount > 0
                            ? `${order.outOfStockCount} item${order.outOfStockCount !== 1 ? 's' : ''} out of stock`
                            : null,
                          order.dateChangedCount > 0
                            ? `${order.dateChangedCount} item${order.dateChangedCount !== 1 ? 's' : ''} rescheduled`
                            : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </p>
                    )}
                  </div>
                </button>
              )
            })}
          </div>
        )}

        {/* Pagination */}
        {totalCount > PAGE_SIZE && (
          <Pagination
            currentPage={currentPage}
            totalItems={totalCount}
            itemsPerPage={PAGE_SIZE}
            onPageChange={setCurrentPage}
            hasNextPage={hasNextPage}
            hasPrevPage={hasPrevPage}
            isLoading={isLoading}
            className="mt-8"
          />
        )}
      </main>
    </EngineerLayout>
  )
}
